import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowLeft, BoxArrowRight, BoxArrowUpRight, Camera, Image as ImageIcon } from 'react-bootstrap-icons';
import { useLocale } from '../../i18n/LocaleContext';
import { createBrand, errorInfo } from '../../api/onboarding';
import { normalizeDomain } from '../embed/embedCode';
import { BRAND_IMAGE_ACCEPT, PARTNER_TERMS_URL, PARTNER_TERMS_VERSION } from '../../config/onboarding';
import { SHOW_SHOPIFY_APP_TUTORIAL } from '../../config/embedFlags';
import { brandErrorField, cleanName, imageProblem, validateBrandForm, BRAND_NAME_MAX } from './brandForm';
import '../embed/Embed.scss';
import '../billing/CardGate.scss';
import './Onboarding.scss';

// "Új márka létrehozása": a brand without Shopify, in two steps —
//   1. this page: name, logo, cover, the webshop address, the partner terms
//      → POST /brands (multipart);
//   Billing is no longer a gate (CEO, 2026-11): after creating the brand the
//   merchant lands straight in the portal; the "!" on Számlázás nudges them
//   to set up billing when they want to.
// The Shopify connect stays behind SHOW_SHOPIFY_APP_TUTORIAL (off while the
// Outfino Shopify app is not approved).

/// A picked image with its preview and the client-side check.
function usePickedImage() {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [problem, setProblem] = useState(null);
  const url = useRef(null);
  useEffect(() => () => url.current && URL.revokeObjectURL(url.current), []);
  const pick = useCallback((e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    const bad = imageProblem(f);
    setProblem(bad);
    if (bad) return;
    if (url.current) URL.revokeObjectURL(url.current);
    url.current = URL.createObjectURL(f);
    setFile(f);
    setPreview(url.current);
  }, []);
  return { file, preview, problem, setProblem, pick };
}

function retryMinutes(seconds) {
  return Math.max(1, Math.ceil(Number(seconds) / 60));
}

export default function NewBrandPage({ onCreated, onCancel, onShopify, onLogout }) {
  const { t } = useLocale();
  const [name, setName] = useState('');
  const [domain, setDomain] = useState('');
  const [terms, setTerms] = useState(false);
  const logo = usePickedImage();
  const cover = usePickedImage();
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState(null); // { key, vars }
  const [busy, setBusy] = useState(false);
  const [tried, setTried] = useState(false);
  const firstError = useRef(null);
  const coverInput = useRef(null);

  // Keyboard: the cover is a focusable label (role=button); Enter/Space open
  // the file picker, the same as a click. A native <label> does nothing on
  // Enter/Space, so we trigger the input ourselves.
  function coverKey(e) {
    if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
      e.preventDefault();
      coverInput.current?.click();
    }
  }

  const shownName = cleanName(name) || t('onboarding.namePlaceholder');
  const normalized = normalizeDomain(domain);

  // Picking a valid file clears that field's error; a refused one sets it.
  useEffect(() => {
    setErrors((e) => ({ ...e, logo: logo.problem || (logo.file ? undefined : e.logo) }));
  }, [logo.problem, logo.file]);
  useEffect(() => {
    setErrors((e) => ({ ...e, cover: cover.problem || (cover.file ? undefined : e.cover) }));
  }, [cover.problem, cover.file]);

  function fieldError(key) {
    const code = errors[key];
    if (!code) return null;
    return (
      <p className="onb-error" role="alert" id={`onb-${key}-error`} data-testid={`onb-error-${key}`}>
        {t(`onboarding.error.${key}.${code}`, { max: BRAND_NAME_MAX })}
      </p>
    );
  }

  function focusFirst(errs) {
    const order = ['logo', 'cover', 'name', 'domain', 'terms'];
    const key = order.find((k) => errs[k]);
    if (!key) return;
    const el = document.querySelector(`[data-field="${key}"]`);
    el?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
    el?.focus?.({ preventScroll: true });
  }

  async function submit(e) {
    e.preventDefault();
    if (busy) return;
    setTried(true);
    setFormError(null);
    const { errors: errs, value } = validateBrandForm({ name, logo: logo.file, cover: cover.file, domain, terms });
    if (logo.problem) errs.logo = logo.problem;
    if (cover.problem) errs.cover = cover.problem;
    setErrors(errs);
    if (Object.keys(errs).length) {
      focusFirst(errs);
      return;
    }
    setBusy(true);
    try {
      const result = await createBrand({
        name: value.name,
        websiteDomain: value.websiteDomain,
        logo: logo.file,
        cover: cover.file,
        termsVersion: PARTNER_TERMS_VERSION,
      });
      await onCreated(result);
    } catch (err) {
      const info = errorInfo(err);
      const field = brandErrorField(info);
      if (field && info.status === 400) {
        const code = {
          INVALID_NAME: 'invalid',
          INVALID_DOMAIN: 'invalid',
          TERMS_NOT_ACCEPTED: 'required',
          INVALID_TERMS_VERSION: 'version',
          LOGO_REQUIRED: 'required',
          COVER_REQUIRED: 'required',
          UNSUPPORTED_IMAGE_TYPE: 'type',
          IMAGE_TOO_LARGE: 'size',
          IMAGE_CORRUPT: 'corrupt',
          UNEXPECTED_FILE_FIELD: 'corrupt',
        }[info.code] || 'invalid';
        const next = { [field]: code };
        setErrors(next);
        focusFirst(next);
      } else if (info.code === 'BRAND_LIMIT_REACHED' || info.status === 409) {
        setFormError({ key: 'onboarding.error.limit' });
      } else if (info.status === 429) {
        setFormError(info.retryAfter
          ? { key: 'onboarding.error.rateLimitedFor', vars: { min: retryMinutes(info.retryAfter) } }
          : { key: 'onboarding.error.rateLimited' });
      } else if (info.code === 'GUEST_NOT_ALLOWED') {
        setFormError({ key: 'onboarding.error.guest' });
      } else if (info.status === 401) {
        setFormError({ key: 'onboarding.error.signedOut' });
      } else {
        setFormError({ key: 'onboarding.error.failed' });
      }
      setBusy(false);
    }
  }

  useEffect(() => {
    if (formError) firstError.current?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
  }, [formError]);

  return (
    <div className="gate onb" data-testid="new-brand">
      <form className="gate__card onb__card" onSubmit={submit} noValidate>
        <h1 className="gate__title">{t('onboarding.title')}</h1>
        <p className="gate__lead">{t('onboarding.lead')}</p>

        {formError && (
          <div className="onb-banner" role="alert" ref={firstError} data-testid="onb-form-error">
            {t(formError.key, formError.vars)}
          </div>
        )}

        {/* Logo + cover, laid out the way the brand page shows them. */}
        <div className="onb-media">
          {/* The whole cover is one big tap target: a <label> wrapping the
              hidden file input, so a click/tap anywhere on the banner opens the
              picker. The logo (below) sits above it with its own input, so taps
              on the logo circle still reach the logo, not the cover. */}
          <label
            className={`onb-cover${errors.cover ? ' is-invalid' : ''}`}
            data-field="cover"
            role="button"
            tabIndex={0}
            onKeyDown={coverKey}
            aria-label={t(cover.preview ? 'onboarding.coverChange' : 'onboarding.coverAdd')}
            title={t(cover.preview ? 'onboarding.coverChange' : 'onboarding.coverAdd')}
          >
            {cover.preview ? (
              <img src={cover.preview} alt="" />
            ) : (
              <span className="onb-cover__ph">
                <ImageIcon size={22} aria-hidden="true" />
                {t('onboarding.coverEmpty')}
              </span>
            )}
            <span className="onb-pick onb-pick--cover">
              <Camera size={14} aria-hidden="true" />
              {t(cover.preview ? 'onboarding.coverChange' : 'onboarding.coverAdd')}
            </span>
            <input ref={coverInput} type="file" accept={BRAND_IMAGE_ACCEPT} onChange={cover.pick} hidden data-testid="onb-cover" />
          </label>
          <div className="onb-id">
            <label
              className={`onb-logo${errors.logo ? ' is-invalid' : ''}`}
              data-field="logo"
              tabIndex={-1}
              title={t(logo.preview ? 'onboarding.logoChange' : 'onboarding.logoAdd')}
            >
              {logo.preview ? <img src={logo.preview} alt="" /> : <span aria-hidden="true">{shownName.charAt(0).toUpperCase()}</span>}
              <span className="onb-logo__cam"><Camera size={13} aria-hidden="true" /></span>
              <input type="file" accept={BRAND_IMAGE_ACCEPT} onChange={logo.pick} hidden data-testid="onb-logo" />
            </label>
            <div className="onb-id__text">
              <span className="onb-id__name" translate="no">{shownName}</span>
              <span className="onb-id__hint">{t('onboarding.mediaHint')}</span>
            </div>
          </div>
          {fieldError('logo')}
          {fieldError('cover')}
        </div>

        <label className="field onb-field">
          <span>{t('onboarding.name')}</span>
          <input
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (errors.name) setErrors((x) => ({ ...x, name: undefined }));
            }}
            maxLength={BRAND_NAME_MAX + 20}
            autoComplete="organization"
            aria-invalid={errors.name ? true : undefined}
            aria-describedby={errors.name ? 'onb-name-error' : undefined}
            data-field="name"
            data-testid="onb-name"
          />
        </label>
        {fieldError('name')}

        <label className="field onb-field">
          <span>{t('onboarding.domain')}</span>
          <input
            value={domain}
            onChange={(e) => {
              setDomain(e.target.value);
              if (errors.domain) setErrors((x) => ({ ...x, domain: undefined }));
            }}
            onBlur={() => {
              if (tried && domain) {
                const r = normalizeDomain(domain);
                setErrors((x) => ({ ...x, domain: r.error }));
              }
            }}
            placeholder="nova-atelier.hu"
            inputMode="url"
            autoComplete="url"
            spellCheck={false}
            aria-invalid={errors.domain ? true : undefined}
            aria-describedby={errors.domain ? 'onb-domain-error' : 'onb-domain-hint'}
            data-field="domain"
            data-testid="onb-domain"
          />
        </label>
        {fieldError('domain') || (
          <p className="onb-hint" id="onb-domain-hint" data-testid="onb-domain-hint">
            {normalized.domain && normalized.domain !== domain.trim()
              ? t('onboarding.domainSavedAs', { domain: normalized.domain })
              : t('onboarding.domainHint')}
          </p>
        )}

        <label className={`onb-terms${errors.terms ? ' is-invalid' : ''}`}>
          <input
            type="checkbox"
            checked={terms}
            onChange={(e) => {
              setTerms(e.target.checked);
              if (e.target.checked) setErrors((x) => ({ ...x, terms: undefined }));
            }}
            aria-invalid={errors.terms ? true : undefined}
            data-field="terms"
            data-testid="onb-terms"
          />
          <span>
            {t('onboarding.termsBefore')}
            <a href={PARTNER_TERMS_URL} target="_blank" rel="noopener noreferrer" data-testid="onb-terms-link">
              {t('onboarding.termsLink')}
              <BoxArrowUpRight size={11} aria-hidden="true" />
            </a>
            {t('onboarding.termsAfter')}
          </span>
        </label>
        {fieldError('terms')}

        <button type="submit" className="btn-o btn-o--dark gate__cta onb__submit" disabled={busy} data-testid="onb-submit">
          {busy ? t('onboarding.creating') : t('onboarding.submit')}
        </button>
        <p className="gate__fine">{t('onboarding.fine')}</p>

        {SHOW_SHOPIFY_APP_TUTORIAL && onShopify && (
          <p className="onb-shopify">
            {t('onboarding.shopify')}{' '}
            <button type="button" className="emb-inline-btn" onClick={onShopify}>{t('onboarding.shopifyLink')}</button>
          </p>
        )}

        <div className="gate__foot">
          {onCancel ? (
            <button type="button" className="btn-o btn-o--ghost btn-o--sm onb__back" onClick={onCancel} data-testid="onb-cancel">
              <ArrowLeft size={14} aria-hidden="true" />
              {t('onboarding.cancel')}
            </button>
          ) : <span />}
          <button type="button" className="btn-o btn-o--ghost btn-o--sm gate__logout" onClick={onLogout}>
            <BoxArrowRight size={14} aria-hidden="true" />
            {t('shell.logout')}
          </button>
        </div>
      </form>
    </div>
  );
}
